import { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Spacer, Skeleton, Box, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import Tree from '../react-tree/Tree';

import { getNodeIds } from '../../services/portfolios';

import PortfolioTreeWrapper from './PortfolioTreeWrapper';
import PortfolioNodeIcon from './PortfolioNodeIcon';
import PortfolioArrowIcon from './PortfolioArrowIcon';

import SearchBar from '../utils/SearchBar';
import CustomCheckbox from '../forms/CustomCheckbox';

/**
 * Items selection component with search bar and tree view.
 *
 * @param {object} source - source is a portfolio tree node object formatted by mapPortfolios()
 * @param {function} getAssignableItems - get items to display in the tree view
 * @param {string[]} selectedItems - selectedItems is an array of selected item ids.
 * @param {function} setSelectedItems - setSelectedItems() is a function to set selected item ids in parent component.
 * @param {boolean} isDisplayedInModal - if the component is displayed in a modal, it might have a different style.
 * @param {boolean} isDisabled - disabled flag
 * @param {boolean} expandAll - expand all tree nodes by default
 *
 */
const ItemsSelectionComponent = ({
    source,
    getAssignableItems,
    selectedItems,
    setSelectedItems,
    isDisplayedInModal,
    isDisabled,
    expandAll = false,
}) => {
    const intl = useIntl();

    const [isLoadingError, setIsLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [nodes, setNodes] = useState([]);
    const [filteredNodes, setFilteredNodes] = useState([]);
    const [expanded, setExpanded] = useState([]);
    const [focused, setFocused] = useState(null);

    const [search, setSearch] = useState('');

    // Get the list of nodes with item ids that are already selected in the portfolio,
    // items cannot be added again, so selection check box will be disabled for those items in tree view.
    const itemsInSelectedPortfolio = useMemo(() => {
        // source is a portfolio tree node object formatted by mapPortfolios()
        return source.nodes
            .filter((node) => !node.isFolder && node.nodes.length > 0)
            .flatMap((group) => group.nodes.map((item) => item.properties?.id));
    }, [source]);

    useEffect(() => {
        setIsLoading(true);

        getAssignableItems()
            .then((items) => {
                setFocused(items[0].id);
                setNodes(items);
                setFilteredNodes(items);

                if (expandAll) {
                    setExpanded(getNodeIds(items));
                }
            })
            .catch(() => {
                setIsLoadingError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [expandAll, getAssignableItems]);

    useEffect(() => {
        const term = search.trimStart().toLowerCase();

        if (term && term.length >= 2) {
            const data = nodes.reduce((acc, group) => {
                const filtered = group.nodes.filter((node) => node.properties?.name.toLowerCase().includes(term));

                if (filtered.length > 0) {
                    acc.push({
                        ...group,
                        nodes: filtered,
                    });
                }

                return acc;
            }, []);
            const ids = getNodeIds(data);

            setFilteredNodes(data);
            setExpanded(ids);
        } else {
            setFilteredNodes(nodes);
            setExpanded([]);
        }
    }, [search]); // eslint-disable-line

    const onAddItemSelectChange = useCallback(
        (node) => {
            const id = node.id;

            const isItemGroup = nodes.some((group) => group.id === node.id);
            const isInSelectedPortfolio = itemsInSelectedPortfolio.includes(parseInt(node.id, 10));

            if (isItemGroup || isInSelectedPortfolio) {
                return;
            }

            setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : prev.concat(id)));
        },
        [nodes, itemsInSelectedPortfolio, setSelectedItems]
    );

    const onExpandChange = useCallback(({ id }) => {
        setExpanded((prev) => (prev.includes(id) ? prev.filter((node) => node !== id) : prev.concat(id)));
    }, []);

    const onFocusChange = useCallback(({ id }) => {
        setFocused(id);
    }, []);

    const onClickToggleExpandCollapse = useCallback((node) => {
        // Expand or collapse a node on label click
        if (node?.isExpandable) {
            setExpanded((prev) =>
                prev.includes(node.id) ? prev.filter((id) => id !== node.id) : prev.concat(node.id)
            );
            setFocused(node.id);
        }
    }, []);

    const renderAddItemsLabel = useCallback(
        (node) => {
            const isInSelectedPortfolio = itemsInSelectedPortfolio.includes(parseInt(node.properties?.id, 10));
            const isSelected = selectedItems.includes(node.id);

            return (
                <div
                    className={`${node?.isExpandable ? '' : 'non-expandable '}portfolio-label-wrapper`}
                    onClick={() => onClickToggleExpandCollapse(node)}
                >
                    {node?.isExpandable && (
                        <span>
                            <PortfolioArrowIcon isExpanded={node?.isExpanded} isMaster={node.properties?.isMaster} />
                        </span>
                    )}

                    <div
                        className="portfolio-label"
                        onClick={(event) => {
                            if (event.shiftKey && node?.isExpandable) {
                                // parent div will take care of expand/collapse
                            } else {
                                onAddItemSelectChange(node);
                            }
                        }}
                    >
                        <PortfolioNodeIcon
                            isFolder={node.isFolder}
                            isExpanded={node?.isExpanded}
                            isMaster={node.properties?.isMaster}
                            typeId={node.properties?.typeId}
                        />

                        <span>{node.properties?.name}</span>
                    </div>

                    {node.nodes.length === 0 && (
                        <CustomCheckbox
                            tabIndex={-1}
                            onChange={() => onAddItemSelectChange(node)}
                            isDisabled={isInSelectedPortfolio}
                            isChecked={isInSelectedPortfolio ? true : isSelected}
                        />
                    )}
                </div>
            );
        },
        [itemsInSelectedPortfolio, selectedItems, onAddItemSelectChange, onClickToggleExpandCollapse]
    );

    return (
        <>
            <SearchBar
                textHolder={intl.formatMessage({ id: 'portfolios_search_items' })}
                onChange={(value) => setSearch(value)}
            />

            <Spacer mb={5} />

            <PortfolioTreeWrapper isDisplayedInModal={isDisplayedInModal} isDisabled={isDisabled}>
                {isLoading ? (
                    Array.from({ length: 15 }).map((_, index) => (
                        <Skeleton key={index}>
                            <Box h="34px" mt={2}>
                                <FormattedMessage id="common_loading" />
                            </Box>
                        </Skeleton>
                    ))
                ) : isLoadingError ? (
                    <Box mx={6}>
                        <Alert status="error">
                            <AlertIcon />

                            <Box>
                                <AlertTitle textTransform="capitalize">
                                    <FormattedMessage id="common_error" />
                                </AlertTitle>

                                <AlertDescription>
                                    <FormattedMessage id="common_loading_error" values={{ items: 'items' }} />
                                </AlertDescription>
                            </Box>
                        </Alert>
                    </Box>
                ) : (
                    <Tree
                        aria-label={intl.formatMessage({ id: 'common_items' })}
                        nodes={filteredNodes}
                        focused={focused}
                        onFocusChange={onFocusChange}
                        expanded={expanded}
                        onExpandChange={onExpandChange}
                        onSelectChange={onAddItemSelectChange}
                        renderLabel={renderAddItemsLabel}
                        expandOnSelect={false}
                    />
                )}
            </PortfolioTreeWrapper>
        </>
    );
};

export default ItemsSelectionComponent;
